<script setup lang="ts">
import { GameAssetTypes } from '@game/types'
import { computed, mediaUrl, ref, shallowRef, useGame, useGameAssets, useHeroGames, useI18n, useIntervalFn, useNimbusConfig, useSwipe, useThemeLogo } from '#imports'

const { components } = useNimbusConfig()
const { t } = useI18n()

const config = components?.LobbyHero
const messages = config?.messages ?? []
const intervalMs = config?.intervalMs ?? 8000
const animateEyebrow = config?.animateEyebrow ?? false

const brandIcon = useThemeLogo('icon')
const eyebrowLogoPath = computed(() => (config?.eyebrowLogoPath ? mediaUrl(config.eyebrowLogoPath) : brandIcon.value?.src || ''))

const { start } = useGame()
const { firstAvailable } = useGameAssets()
const heroGames = useHeroGames()

const slides = computed(() => messages.map((message) => {
    const game = message.gameSlug ? heroGames.bySlug.value.get(message.gameSlug) : undefined
    const gameImage = !message.imagePath && game
        ? firstAvailable(game, GameAssetTypes.TILE, GameAssetTypes.HERO_BACKGROUND)
        : null
    const image = (message.imagePath ? mediaUrl(message.imagePath) : '') || gameImage || ''
    const logo = message.addLogo && game ? firstAvailable(game, GameAssetTypes.FEATURED_LOGO, GameAssetTypes.LOGO) : null
    const ctaLabel = message.cta?.label || (game ? t('nimbus.lobbyHero.playCta', { game: game.name }) : '')

    return {
        ...message,
        game,
        image,
        logo,
        isGameImage: !!gameImage,
        eyebrow: message.badge || game?.name || '',
        ctaLabel,
        ctaHref: message.cta?.href || (message.gameSlug ? '/playgame' : '')
    }
}))

type LobbyHeroSlide = (typeof slides.value)[number]

// Keep the game-tile column width stable while a non-game slide fades in, so the outgoing tile doesn't jump right.
const hasGameSlides = computed(() => slides.value.some(slide => slide.isGameImage))

const lobbyRef = ref<HTMLElement | null>(null)
const activeMessageIndex = shallowRef(0)
const activeSlide = computed(() => slides.value[activeMessageIndex.value])

const { pause, resume } = useIntervalFn(() => {
    if (!slides.value.length) return
    activeMessageIndex.value = (activeMessageIndex.value + 1) % slides.value.length
}, intervalMs)

function showSlide(index: number) {
    const count = slides.value.length
    if (!count) return
    activeMessageIndex.value = (index + count) % count
    pause()
    resume()
}

function onCtaClick(slide: LobbyHeroSlide, event: MouseEvent) {
    if (!slide.game) return
    event.preventDefault()
    start({ gameSlug: slide.game.slug, providerSlug: slide.game.provider_slug, game: slide.game })
}

useSwipe(lobbyRef, {
    threshold: 40,
    onSwipeEnd(_event, direction) {
        if (direction === 'left') {
            showSlide(activeMessageIndex.value + 1)
        }
        else if (direction === 'right') {
            showSlide(activeMessageIndex.value - 1)
        }
    },
})
</script>

<template>
    <section
        v-if="activeSlide"
        ref="lobbyRef"
        class="lobby-hero"
        :class="{ 'lobby-hero--game': hasGameSlides }"
    >
        <div class="lobby-hero__background" />

        <div class="lobby-hero__section lobby-hero__section--copy">
            <div class="lobby-hero__slot out-in-wrap">
                <Transition name="fade">
                    <p :key="animateEyebrow ? activeMessageIndex : 'eyebrow'" class="lobby-hero__eyebrow">
                        <NuxtImg
                            v-if="eyebrowLogoPath"
                            class="lobby-hero__eyebrow-logo"
                            alt="Lobby hero logo"
                            provider="cloudflare"
                            preset="local"
                            :src="eyebrowLogoPath"
                            width="18"
                            height="18"
                            quality="100"
                            loading="eager"
                            decoding="async"
                        />
                        {{ activeSlide.eyebrow }}
                    </p>
                </Transition>
            </div>

            <div class="lobby-hero__slot out-in-wrap">
                <Transition name="fade">
                    <div :key="activeMessageIndex" class="lobby-hero__message">
                        <h2 class="lobby-hero__title">
                            {{ activeSlide.title }}
                            <span v-if="activeSlide.titleHighlight" class="lobby-hero__title-highlight">{{ activeSlide.titleHighlight }}</span>
                        </h2>
                        <p v-if="activeSlide.description" class="lobby-hero__description">
                            {{ activeSlide.description }}
                        </p>

                        <a
                            v-if="activeSlide.ctaLabel"
                            class="lobby-hero__cta"
                            :href="activeSlide.ctaHref || undefined"
                            @click="onCtaClick(activeSlide, $event)"
                        >{{ activeSlide.ctaLabel }}</a>
                    </div>
                </Transition>
            </div>
        </div>

        <div
            class="lobby-hero__section lobby-hero__section--visual out-in-wrap"
            :class="{ 'lobby-hero__section--visual-game': hasGameSlides }"
            aria-hidden="true"
        >
            <span class="sparkle s1" aria-hidden="true" />
            <span class="sparkle s2" aria-hidden="true" />
            <span class="sparkle s3" aria-hidden="true" />
            <span class="sparkle s4" aria-hidden="true" />

            <Transition name="fade">
                <div
                    v-if="activeSlide.image"
                    :key="activeSlide.image"
                    class="lobby-hero__image-frame"
                    :class="{ 'lobby-hero__image-frame--game': activeSlide.isGameImage }"
                    :style="{
                        '--image-scale': activeSlide.scale ?? 1,
                        '--image-offset-y': activeSlide.offsetY ?? '0',
                        '--image-scale-mobile': activeSlide.scaleMobile ?? activeSlide.scale ?? 1,
                        '--image-offset-y-mobile': activeSlide.offsetYMobile ?? activeSlide.offsetY ?? '0',
                    }"
                >
                    <div class="lobby-hero__image-float">
                        <NuxtImg
                            class="lobby-hero__image"
                            :src="activeSlide.image"
                            provider="cloudflare"
                            :preset="activeSlide.isGameImage ? 'gameservice' : 'local'"
                            :width="activeSlide.isGameImage ? 600 : 420"
                            sizes="170px sm:440px md:570px lg:580px"
                            quality="76"
                            loading="eager"
                            decoding="async"
                        />
                    </div>
                </div>
            </Transition>

            <Transition name="fade">
                <NuxtImg
                    v-if="activeSlide.logo"
                    :key="activeSlide.logo"
                    class="lobby-hero__game-logo"
                    :src="activeSlide.logo"
                    :alt="`${activeSlide.game?.name} logo`"
                    provider="cloudflare"
                    preset="gameservice"
                    width="240"
                    sizes="110px sm:200px md:240px"
                    quality="76"
                    loading="eager"
                    decoding="async"
                />
            </Transition>
        </div>

        <div
            v-if="slides.length > 1"
            class="lobby-hero__pagination"
            role="tablist"
        >
            <button
                v-for="(slide, index) in slides"
                :key="index"
                type="button"
                role="tab"
                class="lobby-hero__dot"
                :class="{ 'lobby-hero__dot--active': index === activeMessageIndex }"
                :aria-selected="index === activeMessageIndex"
                :aria-label="t('nimbus.hero.showSlide', { number: index + 1 })"
                @click="showSlide(index)"
            />
        </div>
    </section>
</template>

<style lang="scss" scoped>
.lobby-hero {
    position: relative;
    height: 156px;
    padding: 14px 14px 14px 16px;
    background:
        linear-gradient(90deg, rgb(132 132 132 / 12%) 20%, transparent 80%),
        var(
            --lobby-hero-glow-outer,
            radial-gradient(ellipse 40% 140% at 90% 60%, rgb(79 202 33 / 56%) 0%, transparent 60%)
        ),
        var(
            --lobby-hero-glow-inner,
            radial-gradient(ellipse 60% 240% at 78% 60%, rgb(23 137 78 / 75%) 0%, transparent 65%)
        ),
        var(--background);
    border: var(--lobby-hero-border, none);
    border-radius: var(--border-radius-lg, $border-radius-lg);
    box-shadow: var(--lobby-hero-shadow, none);
    overflow: hidden;
    touch-action: pan-y;
    user-select: none;

    @include tablet {
        height: 240px;
        padding: 32px 0 36px 40px;
    }

    &::after {
        content: '';
        position: absolute;
        inset: -20%;
        z-index: 0;
        pointer-events: none;
        background: linear-gradient(115deg, transparent 24%, rgb(255 255 255 / 5%) 50%, transparent 56%);
        animation: lobby-hero-sheen 9s linear infinite;
    }

    &__background {
        position: absolute;
        inset: 0;
        mix-blend-mode: overlay;
        opacity: 0.07;
        background-image: var(--lobby-hero-carpet, url('/media/landing-page/repeatable-carpet-tile.svg'));
        background-repeat: repeat;
        background-size: 80px 80px;
    }

    &__section {
        position: relative;
        z-index: 1;
        min-width: 0;
    }

    &__slot {
        position: relative;
        width: 100%;
    }

    &__section--copy {
        display: flex;
        height: 100%;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        z-index: 2;
        padding-right: clamp(95px, 30%, 140px);

        @include tablet() {
            padding-right: 0;
        }
    }

    &--game &__section--copy {
        @include tablet() {
            padding-right: calc(0.7 * min(560px, 62%) + 28px);
        }
    }

    &__section--visual {
        position: absolute;
        top: 0;
        right: 10px;
        bottom: 0;
        z-index: 1;
        width: clamp(95px, 30%, 140px);

        @include tablet() {
            right: 0;
            width: 420px;
            max-width: 55%;
        }
    }

    &__section--visual-game {
        right: 0; // Bleed game tiles to right edge on mobile
        width: clamp(150px, 44%, 200px);

        @include tablet {
            width: 560px;
            max-width: 62%;
        }
    }

    &__image-frame {
        position: absolute;
        inset: 0;
    }

    &__image-frame--game {
        .lobby-hero__image-float {
            height: 100%;
            animation: lobby-hero-tile-pulse 8s ease-in-out infinite;
        }

        .lobby-hero__image {
            height: 100%;
            object-fit: cover;
            -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 35%);
            mask-image: linear-gradient(to right, transparent 0%, #000 35%);
        }
    }

    &__image-float {
        animation: lobby-hero-character-float 4.8s ease-in-out infinite;
    }

    &__game-logo {
        position: absolute;
        inset: 0;
        z-index: 2;
        max-width: 60%;
        max-height: 70%;
        height: auto;
        margin: auto;
        object-fit: contain;
        filter: drop-shadow(0 8px 18px rgb(0 0 0 / 55%));

        @include tablet() {
            left: 30%;
        }
    }

    &__image {
        display: block;
        width: 100%;
        height: auto;
        object-position: center top;
        transform: translateY(var(--image-offset-y-mobile, 0)) scale(var(--image-scale-mobile, 1));
        transform-origin: top center;
        filter: drop-shadow(0 16px 26px rgb(0 0 0 / 55%)) drop-shadow(0 0 22px rgb(255 206 1 / 15%));

        @include tablet() {
            transform: translateY(var(--image-offset-y, 0)) scale(var(--image-scale, 1));
        }
    }

    &__eyebrow {
        display: inline-flex;
        align-items: center;
        align-self: flex-start;
        gap: 8px;
        margin: 0 0 8px;
        padding: var(--lobby-hero-eyebrow-padding, 5px 14px 5px 9px);
        background-color: var(--lobby-hero-eyebrow-bg, var(--primary-soft));
        border: var(--lobby-hero-eyebrow-border, 1px solid rgb(254 214 52 / 55%));
        border-radius: var(--border-radius-rounded, 999px);
        color: var(--primary);
        font-size: 11px;
        font-weight: $text-heavy;
        line-height: 1;
        letter-spacing: 0.15em;
        text-transform: uppercase;
    }

    &__eyebrow-logo {
        width: 18px;
        height: 18px;
        flex: 0 0 auto;
        filter: drop-shadow(0 0 4px rgb(254 214 52 / 95%)) drop-shadow(0 0 10px rgb(254 214 52 / 55%));
    }

    &__slot > &__eyebrow {
        // Prevent out-in-wrap fade-leave-active's width:100% from stretching the pill
        width: max-content;
    }

    &__message {
        min-width: 0;
        width: 100%;
    }

    &__title,
    &__description {
        margin: 0;
        font-family: $brand-font-family;
        font-size: 24px;
        font-weight: 900;
        line-height: 1.05;
        text-transform: uppercase;
        overflow-wrap: break-word;
        word-break: break-word;
        hyphens: auto;

        @include tablet() {
            font-size: clamp(40px, 5vw, 48px);
            line-height: 1.025;
            white-space: nowrap;
            overflow-wrap: normal;
            word-break: normal;
            hyphens: manual;
        }
    }

    &__title {
        color: var(--on-surface);
        font-family: var(--lobby-hero-title-font, $brand-font-family);
        font-weight: var(--lobby-hero-title-weight, $text-heavy);
    }

    &--game &__title {
        @include tablet() {
            font-size: clamp(32px, 4vw, 48px);
            white-space: normal;
            text-wrap: balance;
        }
    }

    &__description {
        color: var(--primary-light);
    }

    &__title-highlight {
        color: var(--primary);
    }

    &__cta {
        display: inline-flex;
        align-items: center;
        align-self: flex-start;
        margin-top: 10px;
        padding: 9px 18px;
        background-color: var(--primary);
        border-radius: var(--border-radius, $border-radius);
        color: var(--on-primary);
        font-size: $font-size-xs;
        font-weight: $text-heavy;
        line-height: 1;
        letter-spacing: 0.06em;
        text-decoration: none;
        text-transform: uppercase;
        white-space: nowrap;
        transition:
            background-color 180ms $easeOutQuad,
            transform 180ms $easeOutQuad;

        &:hover {
            background-color: var(--primary-hover, var(--primary-dark));
            transform: translateY(-1px);
        }

        @include tablet {
            margin-top: 16px;
            padding: 12px 24px;
            font-size: $font-size-sm;
        }
    }

    &__pagination {
        position: absolute;
        right: 10px;
        bottom: 10px;
        z-index: 3;
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 4px 7px;
        background: rgb(0 0 0 / 45%);
        border-radius: var(--border-radius-rounded, 999px);
        backdrop-filter: blur(6px);

        @include tablet {
            right: 24px;
            bottom: 18px;
            gap: 8px;
            padding: 6px 10px;
        }
    }

    &__dot {
        width: 6px;
        height: 6px;
        padding: 0;
        background: rgb(255 255 255 / 35%);
        border: 0;
        border-radius: var(--border-radius-rounded, 999px);
        cursor: pointer;
        transition:
            width 220ms $easeOutQuad,
            background-color 220ms $easeOutQuad;

        &:hover {
            background: rgb(255 255 255 / 60%);
        }

        &--active {
            width: 16px;
            background: var(--primary);
            box-shadow: 0 0 8px rgb(255 206 1 / 70%);
        }

        @include tablet {
            width: 8px;
            height: 8px;

            &--active {
                width: 22px;
            }
        }
    }
}

.sparkle {
    position: absolute;
    z-index: 2;
    width: 6px;
    height: 6px;
    border-radius: var(--border-radius-rounded, 999px);
    background: var(--primary);
    box-shadow:
        0 0 6px rgb(255 230 90 / 100%),
        0 0 16px rgb(255 206 1 / 95%),
        0 0 32px rgb(255 206 1 / 70%),
        0 0 56px rgb(255 206 1 / 40%);
    pointer-events: none;
    animation: lobby-hero-sparkle-pulse 3.6s ease-in-out infinite;

    &.s1 {
        top: 14px;
        left: 10px;

        @include tablet {
            top: 30px;
            left: 140px;
        }
    }

    &.s2 {
        top: 10px;
        right: 12px;
        width: 4px;
        height: 4px;
        animation-duration: 4.2s;
        animation-delay: -1.2s;

        @include tablet {
            top: 40px;
            right: 30px;
        }
    }

    &.s3 {
        bottom: 28px;
        left: 18px;
        width: 5px;
        height: 5px;
        animation-duration: 3s;
        animation-delay: -2.4s;

        @include tablet {
            bottom: 120px;
            left: 120px;
        }
    }

    &.s4 {
        right: 14px;
        bottom: 14px;
        width: 5px;
        height: 5px;
        animation-duration: 4.8s;
        animation-delay: -0.6s;

        @include tablet {
            right: 20px;
            bottom: 50px;
        }
    }
}

@keyframes lobby-hero-sheen {
    0% {
        transform: translateX(-100%);
    }

    100% {
        transform: translateX(100%);
    }
}

@keyframes lobby-hero-character-float {
    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-4px);
    }
}

@keyframes lobby-hero-tile-pulse {
    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.02);
    }
}

@keyframes lobby-hero-sparkle-pulse {
    0%,
    100% {
        transform: scale(0.6);
    }

    50% {
        transform: scale(1.2);
    }
}
</style>
